import { formatDataverseRecordKey } from '../lib/dataverseRecordId';
import { normalizeMobileNumber } from '../config/keyRequest';
import { createKeys, revokeAllKeysByMobile } from './unlocKeyService';

/** Match MobilLogon default timed key duration (minutes). */
const MOBIL_LOGON_DURATION_MINUTES = 120;
const GUI_DEVICE_ID = 'opencare-gui';

function sessionActor(account, phone, { sessionUserName } = {}) {
  const typedName = String(sessionUserName || '').trim();

  // Manual GUI logon: store the named phone user, not the OpenCare admin Entra account.
  if (typedName) {
    return {
      userId: GUI_DEVICE_ID,
      email: '',
      name: typedName,
      deviceId: GUI_DEVICE_ID
    };
  }

  const email = account?.username || phone?.lastEmail || '';
  const name =
    account?.name ||
    phone?.lastName ||
    phone?.name ||
    email ||
    phone?.mobileNumber ||
    'OpenCare';
  const userId = account?.localAccountId || account?.homeAccountId || phone?.lastUserId || GUI_DEVICE_ID;

  return {
    userId: String(userId),
    email: String(email),
    name: String(name),
    deviceId: GUI_DEVICE_ID
  };
}

/**
 * Manual phone session from React — same side effects as MobilLogon / MobilLogoff:
 * oc_mobillog, oc_telefon session fields, tilgangsgrupper + sync, Unloc CreateKeys / RevokeKeys.
 */
export function createPhoneSessionService({
  dataverseApiCall,
  phoneService,
  accessGroupService,
  lockService,
  mobilLogService
}) {
  const patchPhoneSession = async (phoneId, fields) => {
    await dataverseApiCall(`/oc_telefons(${formatDataverseRecordKey(phoneId)})`, {
      method: 'PATCH',
      body: JSON.stringify(fields)
    });
  };

  const reloadPhone = async (phoneId) => {
    const all = await phoneService.getAll({ includeInactive: true });
    return all.find((p) => p.phoneId === phoneId) || null;
  };

  const unlocLockIds = async () => {
    const locks = await lockService.getAll();
    return (locks || [])
      .filter((lock) => lock.provider === 'unloc' || String(lock.vendor || '').toLowerCase() === 'unloc')
      .map((lock) => lock.externalId || lock.lockId)
      .filter(Boolean);
  };

  return {
    logon: async (phone, { account, sessionUserName } = {}) => {
      const mobile = normalizeMobileNumber(phone.mobileNumber) || phone.mobileNumber;
      if (!mobile) {
        throw new Error('Telefon mangler gyldig mobilnummer.');
      }

      const typedName = String(sessionUserName || '').trim();
      if (!typedName) {
        throw new Error('Oppgi navn på pålogget bruker.');
      }

      const actor = sessionActor(account, phone, { sessionUserName: typedName });
      const timestamp = new Date().toISOString();

      await mobilLogService.create({
        direction: false,
        mobileNumber: mobile,
        ...actor,
        timestamp
      });

      await patchPhoneSession(phone.phoneId, {
        oc_is_logged_in: true,
        oc_last_logon: timestamp,
        oc_last_device_id: actor.deviceId,
        oc_last_user_id: actor.userId,
        oc_last_email: actor.email || '',
        oc_last_name: actor.name
      });

      await accessGroupService.enrollNewPhoneInDefaultGroups(phone.phoneId, mobile);

      const lockIds = await unlocLockIds();
      const durationMinutes = phone.lagreNokler ? undefined : MOBIL_LOGON_DURATION_MINUTES;
      const keysResult = await createKeys({
        appUserId: mobile,
        lockIds,
        durationMinutes
      });

      if (keysResult === null) {
        throw new Error('Unloc-token mangler — kunne ikke utstede nøkler.');
      }

      return reloadPhone(phone.phoneId);
    },

    logoff: async (phone, { account } = {}) => {
      const mobile = normalizeMobileNumber(phone.mobileNumber) || phone.mobileNumber;
      if (!mobile) {
        throw new Error('Telefon mangler gyldig mobilnummer.');
      }

      const actor = sessionActor(account, phone);
      const timestamp = new Date().toISOString();

      await mobilLogService.create({
        direction: true,
        mobileNumber: mobile,
        ...actor,
        timestamp
      });

      await patchPhoneSession(phone.phoneId, {
        oc_is_logged_in: false,
        oc_last_logoff: timestamp
      });

      await accessGroupService.removePhoneFromAllGroups(phone.phoneId, mobile);

      const revokeResult = await revokeAllKeysByMobile(mobile);
      if (revokeResult === null) {
        throw new Error('Unloc-token mangler — kunne ikke trekke tilbake nøkler.');
      }

      return reloadPhone(phone.phoneId);
    }
  };
}
